import React from "react";
import {Checkbox, Label, TextInput} from "flowbite-react";
import {isNullOrUndefined} from "helpers/is-null-or-undefined";
import {MultiChoiceSelect_SurveyQuestionPage_Options, MultiSelectValue} from "data/models/survey/multi-choice";
import {RenderableTitle, SurveySectionsRenderingModel} from "data/models/survey/base";
import {
  CHOICE_ITEM_CLASS,
  DESCRIPTION_CLASS,
  QUESTION_CONTAINER_CLASS,
  TEXT_INPUT_THEME,
  TITLE_CLASS
} from "components/Survey/classes";
import {sortChoiceValues} from "helpers/sort-choice-values";
import {TitleComponent} from "components/Survey/survey-sections-rendering-components/SurveySection";

export type ChoiceSelectProps = {
  options: MultiChoiceSelect_SurveyQuestionPage_Options,
  questionId: string,
  title: RenderableTitle,
  description: RenderableTitle | null,
  surveySectionsRenderingModel: SurveySectionsRenderingModel | null,
  value: MultiSelectValue | null | undefined,
  onChange: (id: string, value: MultiSelectValue | null) => void,
}

const OTHER_CHOICE_KEY = "reserved:other";

export default function MultiChoiceSelectSurveyQuestionPage(
    {
      options,
      title,
      description,
      questionId,
      surveySectionsRenderingModel,
      value,
      onChange
    }: ChoiceSelectProps
) {
  function handleChange(changedValue: string) {
    if (isNullOrUndefined(value) || isNullOrUndefined(value.choice)) {
      const newValues = [changedValue];
      onChange(questionId, {
        choice: {
          values: newValues,
        }
      });
    } else if (value.choice.values === undefined) {
      const newValues = [changedValue];
      const otherObject = value.choice.other !== undefined ? {other: value.choice.other} : {};
      onChange(questionId, {
        ...value,
        choice: {
          values: newValues,
          ...otherObject,
        },
      });
    } else if (value.choice.values.includes(changedValue)) {
      const newValues = value.choice.values.filter(v => v !== changedValue);
      const valuesObject = newValues.length !== 0 ? {values: newValues,} : {};
      const otherObject = value.choice.other !== undefined ? {other: value.choice.other} : {};
      onChange(questionId, newValues.length !== 0 || value.choice.other !== undefined || value.enabled ? {
        ...value,
        choice: {
          ...valuesObject,
          ...otherObject
        },
      } : null);
    } else {
      const newValues = [...value.choice.values, changedValue];
      const otherObject = value.choice.other !== undefined ? {other: value.choice.other} : {};
      onChange(questionId, {
        ...value,
        choice: {
          values: newValues,
          ...otherObject
        },
      });
    }
  }

  function handleChangeOther(otherValue: string) {
    if (isNullOrUndefined(value)) {
      throw new Error("Value unexpectedly undefined");
    }
    const valuesObject = value.choice?.values?.length !== 0 ? {values: value.choice?.values,} : {};
    onChange(questionId, {
      ...value,
      choice: {
        ...valuesObject,
        ...(otherValue !== '' ? {other: otherValue} : {}),
      },
    })
  }

  function handleChangeEnabled(enabledValue: string) {
    const choiceObject = enabledValue === "yes" ? {
      choice: {
        ...(value?.choice ?? {})
      }
    } : {};
    onChange(questionId, {
      ...choiceObject,
      enabled: (enabledValue === "yes"),
    })
  }

  const shouldShowChoices = (
      options.enablingQuestion !== null && value?.enabled
  ) || options.enablingQuestion === null;

  return <div className={"flex flex-col w-full"}>
    <div className={QUESTION_CONTAINER_CLASS}>
      <fieldset name={questionId} autoFocus={true}>
        {options.enablingQuestion && <div className={"mb-24"}>
            <div className={"my-4 text-center"}><TitleComponent title={options.enablingQuestion.question} surveySectionRenderingModel={surveySectionsRenderingModel} defaultTitleClass={TITLE_CLASS} /></div>
            <div className={"my-6 text-center"}><TitleComponent title={options.enablingQuestion.description} surveySectionRenderingModel={surveySectionsRenderingModel} defaultTitleClass={DESCRIPTION_CLASS} /></div>
            <div className="flex flex-wrap gap-4 justify-center">
                <div className="flex flex-col items-center gap-2">
                    <Checkbox
                        className="hidden peer"
                        id={`${questionId}.yes`}
                        value={"yes"}
                        checked={value?.enabled === true}
                        onChange={({target: {value: changedValue}}) => handleChangeEnabled(changedValue)}
                    />
                    <Label htmlFor={`${questionId}.yes`}
                           className={CHOICE_ITEM_CLASS}>
                        Yes
                    </Label>
                </div>
                <div className="flex flex-col items-center gap-2">
                    <Checkbox
                        className="hidden peer"
                        id={`${questionId}.no`}
                        value={"no"}
                        checked={value?.enabled === false}
                        onChange={({target: {value: changedValue}}) => handleChangeEnabled(changedValue)}
                    />
                    <Label htmlFor={`${questionId}.no`}
                           className={CHOICE_ITEM_CLASS}>
                        No
                    </Label>
                </div>
            </div>
        </div>}
        {shouldShowChoices && <div>
            <div className={"my-4 text-center"}>{title}</div>
          {description !== null && <div className={"my-4 text-center"}>{description}</div>}
            <div className="flex flex-wrap gap-4 mt-8 justify-center">
              {(options.sorted ? sortChoiceValues(options.choices) : options.choices)
                  .map((choice, index) => <div key={index} className="flex flex-col items-center gap-2">
                <Checkbox
                    className="hidden peer"
                    id={`${questionId}.${choice.key}`}
                    value={choice.key}
                    checked={value?.choice?.values?.includes(choice.key) ?? false}
                    onChange={({target: {value: changedValue}}) => handleChange(changedValue)}
                />
                <Label htmlFor={`${questionId}.${choice.key}`}
                       className={CHOICE_ITEM_CLASS}>
                  {choice.value}
                </Label>
              </div>)}
            </div>
          {options.otherInputBox && <div className={"mt-8 text-center"}>
              <Label htmlFor={`${questionId}.${OTHER_CHOICE_KEY}`}
                     className={"inline-flex py-1 px-10 cursor-pointer"}>
                  <span className={"mr-4 my-auto text-lg font-normal text-neutral-500"}>Other</span>
                  <TextInput
                      className={"md:min-w-80"}
                      id={questionId}
                      theme={TEXT_INPUT_THEME}
                      sizing={"lg"}
                      type={"text"}
                      value={value?.choice?.other ?? ""}
                      onChange={({target: {value: editValue}}) => handleChangeOther(editValue)}
                  />
              </Label>
          </div>}
        </div>}
      </fieldset>
    </div>
  </div>
}
